import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { debounce } from 'lodash';

import { db } from '../firebase';
import { useUser } from '../UserContext';

function UserSearch({ onSelectUser }) {
  const [searchTerm, setSearchTerm] = useState(''); // Search input state
  const [users, setUsers] = useState([]); // List of search results
  const [loading, setLoading] = useState(false); // Loading indicator
  const { user } = useUser(); // Current user's context data

  // Debounced function to fetch users from Firestore
  const fetchUsers = debounce(async () => {
    if (!searchTerm.trim()) {
      setUsers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const usersRef = collection(db, 'users');

    try {
      const snap = await getDocs(usersRef);

      const allUsers = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id, // Firestore document ID
          fullName: `${data.firstName} ${data.lastName}`, // Combine original first and last name
          normalizedFullName: `${data.firstName.toLowerCase()} ${data.lastName.toLowerCase()}`, // Lowercase for comparison
          email: data.email || '', // Include email (optional for display)
          awayStatus: data.awayStatus || false // Example: away status field
        };
      });

      // Filter users based on case-insensitive search term
      const filteredUsers = allUsers.filter(
        (u) =>
          u.id !== user.uid && // Exclude current user
          u.normalizedFullName.includes(searchTerm.toLowerCase())
      );

      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }

    setLoading(false);
  }, 300); // 300ms debounce delay

  // Trigger the fetchUsers function when the search term changes
  useEffect(() => {
    fetchUsers();
    return () => {
      fetchUsers.cancel(); // Cancel the debounce on unmount
    };
  }, [searchTerm]);

  return (
    <Box sx={{ width: { xs: '100%', sm: 300 }, padding: { xs: 2, sm: 0 } }}>
      <Autocomplete
        options={users}
        getOptionLabel={(u) => `${u.fullName}${u.awayStatus ? ' (Away)' : ''}`} // Show full name + away status
        loading={loading}
        noOptionsText="No users found"
        onInputChange={(e, val) => setSearchTerm(val)} // Update search term
        onChange={(e, newUser) => {
          if (newUser) {
            onSelectUser(newUser.id, newUser.fullName, newUser.awayStatus); // Pass selected user data to parent
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            fullWidth
            placeholder="Search by name..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </Box>
  );
}

export default UserSearch;
